import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Stack,
  alpha,
  useTheme,
} from "@mui/material";
import { Add, Delete, Visibility } from "@mui/icons-material";
import { useQuery } from "react-query";
import SearchableSelect from "../../FormFields/CustomSelect/SearchableSelect";
import CustomInput from "../../FormFields/CustomInput/CustomInput";
import CustomDateTime from "../../CustomForm/CustomDateTime";
import { QK } from "../../../base/qk";
import { searchCandidates } from "../../../api/services/candidate";

const WhoWitnessed = ({ formData, onChange }) => {
  const theme = useTheme();

  const { data: careers, isLoading: careersLoading } = useQuery(
    [QK.searchCareers, ""],
    () => searchCandidates(""),
    { enabled: true, keepPreviousData: true }
  );

  const careerOptions = (careers || []).map((c) => ({
    label: `${c.first_name} ${c.last_name}`.trim(),
    value: c.id,
  }));

  const list = formData.who_witnessed || [];

  const updateList = (value) =>
    onChange({ target: { name: "who_witnessed", value } });

  const handleAdd = () => {
    updateList([
      ...list,
      { witnessed: "", name: "", statement: "", date_time: "" },
    ]);
  };

  const handleDelete = (index) => {
    updateList(list.filter((_, i) => i !== index));
  };

  const handleFieldChange = (e, index, fieldName) => {
    const { value } = e.target;
    updateList(
      list.map((item, i) =>
        i === index ? { ...item, [fieldName]: value } : item
      )
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "8px",
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Visibility
            sx={{ fontSize: 20, color: theme.palette.primary.main }}
          />
        </Box>
        <Box>
          <Typography
            variant="h6"
            fontWeight={700}
            color="#1A1A2E"
            fontSize="0.95rem"
          >
            Who witnessed the incident?
          </Typography>
          <Typography variant="caption" color="#6B7280" fontSize="0.7rem">
            People who saw the incident or have a statement
          </Typography>
        </Box>
      </Box>

      <Stack spacing={2}>
        {list.map((witness, index) => (
          <Box
            key={index}
            sx={{
              border: "1px solid #EAECF0",
              borderRadius: "10px",
              p: 2,
              bgcolor: "#FAFBFC",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                color="#475467"
                fontSize="0.72rem"
                sx={{ textTransform: "uppercase", letterSpacing: "0.3px" }}
              >
                Witness {index + 1}
              </Typography>

              <Stack direction="row" spacing={0.5}>
                <IconButton
                  onClick={handleAdd}
                  size="small"
                  sx={{
                    width: 28,
                    height: 28,
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.15),
                    },
                  }}
                >
                  <Add sx={{ fontSize: 16 }} />
                </IconButton>
                {list.length > 1 && (
                  <IconButton
                    onClick={() => handleDelete(index)}
                    size="small"
                    sx={{
                      width: 28,
                      height: 28,
                      color: "#B42318",
                      bgcolor: "#FEF3F2",
                      "&:hover": { bgcolor: "#FEE4E2" },
                    }}
                  >
                    <Delete sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Stack>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
                <SearchableSelect
                  name={`who_witnessed.${index}.witnessed`}
                  label={careersLoading ? "Loading..." : "Witness"}
                  value={
                    careerOptions.find(
                      (o) => o.value === witness.witnessed
                    ) || null
                  }
                  onChange={(opt) =>
                    handleFieldChange(
                      { target: { value: opt?.value ?? "" } },
                      index,
                      "witnessed"
                    )
                  }
                  options={careerOptions}
                  isLoading={careersLoading}
                  placeholder="Search witness..."
                  getOptionLabel={(o) => o.label}
                  getOptionValue={(o) => o.value}
                />
              </Box>

              <Box
                sx={{
                  flex: "0 0 auto",
                  px: 1,
                  color: "#94A3B8",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                or
              </Box>

              <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
                <CustomInput
                  name={`who_witnessed.${index}.name`}
                  label=" "
                  value={witness.name}
                  onChange={(e) => handleFieldChange(e, index, "name")}
                  placeholder="Type in name"
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" },
                gap: 2,
              }}
            >
              <CustomInput
                name={`who_witnessed.${index}.statement`}
                label="Add statement"
                value={witness.statement}
                onChange={(e) => handleFieldChange(e, index, "statement")}
                multiline
                rows={2}
                placeholder="Add statement"
              />

              <CustomDateTime
                name={`who_witnessed.${index}.date_time`}
                label="Date and Time Recorded"
                value={witness.date_time}
                onChange={(e) => handleFieldChange(e, index, "date_time")}
              />
            </Box>
          </Box>
        ))}
      </Stack>

      {list.length === 0 && (
        <Box
          onClick={handleAdd}
          sx={{
            textAlign: "center",
            py: 4,
            border: "2px dashed #E2E8F0",
            borderRadius: "10px",
            bgcolor: "#F8FAFC",
            cursor: "pointer",
            transition: "all 0.2s",
            "&:hover": {
              borderColor: theme.palette.primary.main,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            },
          }}
        >
          <IconButton
            size="large"
            sx={{ color: theme.palette.primary.main, mb: 0.5 }}
          >
            <Add />
          </IconButton>
          <Typography
            variant="body2"
            color="#6B7280"
            fontSize="0.8rem"
            fontWeight={500}
          >
            No witness added yet. Click to add a witness.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default WhoWitnessed;